import { NgForOf, NgIf } from '@angular/common';
import { Component, computed, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UtcToUserFormattedPipe } from '../../../../core/pipes/utc-to-user-formatted.pipe';
import { MatPillComponent } from '../../../../shared/components/mat-pill/mat-pill.component';
import { ELoadStatus } from '../../../../state/state.interface';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { WorkInstructionEditorComponent } from '../../../settings/work-instructions/work-instruction-editor/work-instruction-editor.component';
import { WorkInstructionStepperComponent } from '../../work-instruction-stepper/work-instruction-stepper.component';
import { IWorkInstructionStepperParams } from '../../work-instruction-stepper/work-instruction-stepper.interface';
import { ApplyChangesRecursivelyQuestionModalComponent } from '../apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.component';
import {
  EItemRecurrenceEditType,
  ERecursiveAction,
} from '../apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.interface';
import { RecurringItemService } from '../apply-changes-recursively-question-modal/recurring-item.service';
import { IWorkInstructionItemCard, IWorkInstructionListDialogData } from './item-work-instruction-list.interface';
import { IItemWorkInstructionListState, ItemWorkInstructionListStore } from './item-work-instruction-list.store';

@Component({
  imports: [
    MatExpansionModule,
    TranslateModule,
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatButton,
    MatDialogClose,
    NgForOf,
    MatCard,
    MatCardTitle,
    MatCardHeader,
    MatCardActions,
    MatPillComponent,
    MatCardContent,
    NgIf,
    UtcToUserFormattedPipe,
    MatIcon,
    MatTooltip,
  ],
  providers: [ItemWorkInstructionListStore, HttpUtilitiesService, RecurringItemService],
  selector: 'app-item-work-instruction-list',
  styleUrl: './item-work-instruction-list.component.scss',
  templateUrl: './item-work-instruction-list.component.html',
})
export class ItemWorkInstructionListComponent implements OnInit, OnDestroy {
  protected readonly subscriptions: Subscription[] = [];
  public readonly ELoadStatus = ELoadStatus;

  public items = computed(() => {
    return this.data.onlyOpenMode ? this.store.data$().filter((item) => item.assignmentId) : this.store.data$();
  });

  constructor(
    public dialogRef: MatDialogRef<ItemWorkInstructionListComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IWorkInstructionListDialogData,
    public store: ItemWorkInstructionListStore,
    private readonly dialog: MatDialog,
    private readonly recurringItemService: RecurringItemService,
  ) {}

  public ngOnInit(): void {
    this.getData();

    this.subscriptions.push(
      this.store
        .select((state: IItemWorkInstructionListState) => state.assignmentLoading)
        .pipe(filter((value: ELoadStatus) => value === ELoadStatus.success))
        .subscribe(() => {
          this.getData();
        }),
    );
  }

  public onClickedOpenStepper(workInstructionId: number): void {
    const workInstruction: IWorkInstructionItemCard | undefined = this.store
      .data$()
      .find((item) => item.id === workInstructionId);

    if (!workInstruction) {
      return;
    }

    this.dialog.open(WorkInstructionStepperComponent, {
      ...WorkInstructionEditorComponent.dialogConfig,
      data: {
        ...this.data,
        itemGuideAssignmentId: workInstruction.assignmentId,
        name: workInstruction.name,
        workInstructionId,
      } as IWorkInstructionStepperParams,
    });
  }

  public askApplyChangesRecursively(workInstructionId: number, action: 'assign' | 'unassign'): void {
    if (!this.data.dueDateForRecurrence) {
      if (action === 'assign') {
        this.assignItemWorkInstruction(workInstructionId);

        return;
      }

      this.unassignItemWorkInstruction(workInstructionId);

      return;
    }

    this.subscriptions.push(
      this.dialog
        .open(ApplyChangesRecursivelyQuestionModalComponent, {
          data: { mode: ERecursiveAction.edit },
        })
        .afterClosed()
        .subscribe((selectedRecurrenceEditType: EItemRecurrenceEditType | '' | undefined): void => {
          if (!selectedRecurrenceEditType) {
            return;
          }

          if (action === 'assign') {
            this.assignItemWorkInstruction(workInstructionId, selectedRecurrenceEditType);

            return;
          }

          this.unassignItemWorkInstruction(workInstructionId, selectedRecurrenceEditType);

          return;
        }),
    );
  }

  private assignItemWorkInstruction(
    workInstructionId: number,
    recurrenceType = EItemRecurrenceEditType.onlyThis,
  ): void {
    this.store.assignItemWorkInstruction({
      boardId: this.data.boardId,
      guideId: workInstructionId,
      itemId: this.data.itemId,
      ...this.recurringItemService.getStartDateForAttachmentRecurringUpdate(
        recurrenceType,
        this.data.dueDateForRecurrence,
      ),
    });
  }

  private unassignItemWorkInstruction(
    id: number,
    recurrenceType: EItemRecurrenceEditType = EItemRecurrenceEditType.onlyThis,
  ): void {
    this.store.unassignItemWorkInstruction({
      id,
      ...this.recurringItemService.getStartDateForAttachmentRecurringUpdate(
        recurrenceType,
        this.data.dueDateForRecurrence,
      ),
    });
  }

  public closeDialog(): void {
    this.dialogRef.close(false);
  }

  public ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription?.unsubscribe();
    }
  }

  private getData(): void {
    this.store.loadWorkInstruction({ boardId: this.data.boardId, itemId: this.data.itemId });
  }
}
